import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import uuid from 'uuid/v4';
import getBatchableQueue from '../resilienceQueue';
import getMetricsCollector from '../resilienceQueue/Metrics';
import { attachXidToMultipleEvents } from '../xid';
import { DEFAULT_REQUEST_TIMEOUT } from './defaults';
import { HTTP_STATUS_CODE_MAP } from './HttpStatusCode';
import { sendEvents } from './sendEvents';
import { ALLOW_LISTED_FETCH_ERROR_NAMES, FetchError, LastError, SegmentEventTypes } from './types';
import User from './user';
import { buildContext, prepareEventProperties } from './util';
class AnalyticsAPIResponse {
  constructor(success, validationErrors, message, code, validationReports) {
    this.success = success;
    this.validationErrors = validationErrors;
    this.message = message;
    this.code = code;
    this.validationReports = validationReports;
  }
  isSuccess() {
    return this.success;
  }
  getValidationErrors() {
    return this.validationErrors;
  }
  getMessage() {
    return this.message;
  }
  getCode() {
    return this.code;
  }
  getValidationReports() {
    return this.validationReports === undefined ? null : this.validationReports;
  }
}
class Message {
  constructor(error) {
    this.error = error;
  }
}
class EventValidationReport {
  constructor(messageId, results) {
    this.messageId = messageId;
    this.results = results;
  }
  getMessageId() {
    return this.messageId;
  }
  getResults() {
    return this.results;
  }
}
export class AnalyticsResponse {
  constructor(success, statusCode, message, eventResponseList) {
    this.success = success;
    this.statusCode = statusCode;
    this.message = message;
    this.eventResponseList = eventResponseList;
  }
  isSuccess() {
    return this.success;
  }
  getStatusCode() {
    return this.statusCode;
  }
  getMessage() {
    return this.message;
  }
  getEventResponseList() {
    return this.eventResponseList;
  }
}
class EventResponse {
  constructor(messageId, results, segmentEvent) {
    this.messageId = messageId;
    this.results = results;
    this.segmentEvent = segmentEvent;
  }
  getMessageId() {
    return this.messageId;
  }
  getResults() {
    return this.results;
  }
  getSegmentEvent() {
    return this.segmentEvent;
  }
}
class EventValidationResult {
  constructor(type, severity, propertyPath, message) {
    this.type = type;
    this.severity = severity;
    this.propertyPath = propertyPath;
    this.message = message;
  }
  getType() {
    return this.type;
  }
  getSeverity() {
    return this.severity;
  }
  getPropertyPath() {
    return this.propertyPath;
  }
  getMessage() {
    return this.message;
  }
}
export default class EventProcessor {
  constructor(options) {
    var _this$options;
    // Using anonymous function so it can have the BatchFlushCallback type associated with it
    // And to allow it to not need bind when passing through to ResilieceQueue.
    _defineProperty(this, "sendEvents", async (items, callback) => {
      var _this$lastSendEventsE, _this$metadataClient;
      if (this.options.enableEventCalls === false) {
        return;
      }
      const httpRetryCount = this.resilienceQueue.getGlobalRetryCount();
      const metricsPayload = this.metrics.getMetricsPayload();
      const eventsWithXID = await this.attachXIDs(items);

      // Calculating sentAt after the XID generation as this may take some time.
      const sentAt = new Date().toISOString();
      const metadataInfo = await this.metadataClient.getMetadataAsync();
      const [eventUrl, isFallbackUrl] = this.getEventUrl(metadataInfo);
      const metadata = _objectSpread(_objectSpread({}, metricsPayload), {}, {
        httpRetryCount,
        lastSendEventsErrorReason: ((_this$lastSendEventsE = this.lastSendEventsError) === null || _this$lastSendEventsE === void 0 ? void 0 : _this$lastSendEventsE.message) || null,
        // Temporary back-compat
        lastSendEventsError: this.lastSendEventsError,
        lastUrlFallbackError: this.lastUrlFallbackError,
        isUsingFallbackUrl: isFallbackUrl,
        props: (metadataInfo === null || metadataInfo === void 0 ? void 0 : metadataInfo.props) || null,
        metadataClientMetrics: ((_this$metadataClient = this.metadataClient) === null || _this$metadataClient === void 0 ? void 0 : _this$metadataClient.metrics) || null
      });

      // Remove default values from metadata to save space
      this.cleanLibraryMetadata(metadata);
      const unpackagedEvents = eventsWithXID.map(item => {
        item.msg.sentAt = sentAt;
        return item.msg;
      });
      const batchBody = {
        batch: unpackagedEvents,
        sentAt,
        metadata
      };
      try {
        const response = await sendEvents({
          url: eventUrl,
          batch: batchBody,
          timeout: this.options.requestTimeout
        });
        this.lastSendEventsError = null;
        this.lastUrlFallbackError = null;
        this.metrics.subtractFromMetrics(metricsPayload);
        callback(null, response);
        await this.invokeLocalResponseCallbackAsync(items, response, null);
      } catch (error) {
        // If there's an error, other than one which indicates a temporary problem with the service,
        // we'll stop using the URL from the metadata and fall back to the default URL. After a time
        // we'll fall forward again in case the problem has been resolved.
        if (error instanceof FetchError && [429, 503].includes(error.statusCode)) {
          // Ignore 429 and 503 errors since they're considered to indicate a temporary
          // issue with the service, rather than a network failure due to blocked requests.
          this.lastSendEventsError = new LastError('ServerBusyError', error);
        } else {
          const errorName = error instanceof Error && ALLOW_LISTED_FETCH_ERROR_NAMES.includes(error.name) ? error.name : 'Unknown';
          this.lastSendEventsError = new LastError(errorName, error);

          // Handle falling back to the default URL if we're using the metadata URL.
          this.useUrlFromMetadata = false;

          // If we're now using the fallback URL, and the last attempt wasn't,
          // we'll record this as the error that triggered the fallback.
          const [_, willUseFallbackUrl] = this.getEventUrl(metadataInfo);
          if (willUseFallbackUrl && willUseFallbackUrl !== isFallbackUrl) {
            this.lastUrlFallbackError = this.lastSendEventsError;
          }
        }
        callback(error, null);
        await this.invokeLocalResponseCallbackAsync(items, null, error);
      }
    });
    this.options = _objectSpread(_objectSpread({}, options), {}, {
      requestTimeout: options.requestTimeout || DEFAULT_REQUEST_TIMEOUT,
      retryQueueOptions: options.retryQueueOptions || {},
      logger: options.logger || console,
      disableCookiePersistence: options.disableCookiePersistence || false,
      env: options.env,
      perimeter: options.perimeter || '',
      enableMetadataCalls: typeof options.enableMetadataCalls === 'boolean' ? options.enableMetadataCalls : true,
      enableEventCalls: typeof options.enableEventCalls === 'boolean' ? options.enableEventCalls : true
    });
    this.user = new User((_this$options = this.options) === null || _this$options === void 0 ? void 0 : _this$options.disableCookiePersistence);
    this.xidPromiseCallback = options.xidPromiseGetter();
    this.gasv3BatchUrl = `${options.apiHostProtocol}://${options.apiHost}/batch`;
    this.metrics = getMetricsCollector();
    this.responseCallback = options.responseCallback;
    this.resilienceQueue = getBatchableQueue(options.retryQueuePrefix, options.product, this.options.retryQueueOptions, this.options.logger);
    this.metadataClient = options.metadataClient;
    this.useUrlFromMetadata = true;
    this.lastSendEventsError = null;
    this.lastUrlFallbackError = null;
    setInterval(() => {
      this.useUrlFromMetadata = true;
    }, 10 * 60 * 1000);
    this.resilienceQueue.start(this.sendEvents);
  }
  getUser() {
    return this.user;
  }
  async track(eventName, builtEvent, context, callback) {
    const baseEvent = this.buildBaseEvent(context, SegmentEventTypes.TRACK, builtEvent);
    const eventWithoutMessageId = _objectSpread(_objectSpread({}, baseEvent), {}, {
      type: SegmentEventTypes.TRACK,
      properties: prepareEventProperties(builtEvent),
      event: eventName
    });
    const event = _objectSpread(_objectSpread({}, eventWithoutMessageId), {}, {
      messageId: this.createMessageId()
    });
    const packagedEvent = this.packageEvent(event);
    await this.enqueueEvent(packagedEvent);
    if (callback) {
      callback();
    }
  }
  async page(eventName, builtEvent, context, callback) {
    const baseEvent = this.buildBaseEvent(context, SegmentEventTypes.PAGE, builtEvent);
    const eventWithoutMessageId = _objectSpread(_objectSpread({}, baseEvent), {}, {
      type: SegmentEventTypes.PAGE,
      properties: prepareEventProperties(builtEvent),
      name: eventName
    });
    const event = _objectSpread(_objectSpread({}, eventWithoutMessageId), {}, {
      messageId: this.createMessageId()
    });
    const packagedEvent = this.packageEvent(event);
    await this.enqueueEvent(packagedEvent);
    if (callback) {
      callback();
    }
  }

  // Segment uses the identifier to update user id which we have already done in the analyticsWebClient.ts
  async identify(_identifier, builtEvent, context, callback) {
    const baseEvent = this.buildBaseEvent(context, SegmentEventTypes.IDENTIFY, builtEvent);
    const eventWithoutMessageId = _objectSpread(_objectSpread({}, baseEvent), {}, {
      type: SegmentEventTypes.IDENTIFY,
      traits: prepareEventProperties(builtEvent)
    });
    const event = _objectSpread(_objectSpread({}, eventWithoutMessageId), {}, {
      messageId: this.createMessageId()
    });
    const packagedEvent = this.packageEvent(event);
    await this.enqueueEvent(packagedEvent);
    if (callback) {
      callback();
    }
  }
  async enqueueEvent(packagedEvent) {
    if (this.options.enableMetadataCalls) {
      this.metadataClient.start(false); // Start the metadata client if needed
    }
    await this.resilienceQueue.addItem(packagedEvent);
  }
  buildBaseEvent(context, type, overrides) {
    const clonedContext = prepareEventProperties(context);
    const segmentContext = buildContext(clonedContext);
    return {
      context: segmentContext,
      timestamp: new Date().toISOString(),
      type,
      userId: this.user.getUserId(),
      anonymousId: overrides.anonymousId || this.user.getAnonymousId()
    };
  }
  createMessageId() {
    return `ajs-${uuid()}`;
  }
  packageEvent(event) {
    const {
      apiHost,
      apiHostProtocol
    } = this.options;
    return {
      headers: {
        'Content-Type': 'text/plain'
      },
      msg: event,
      url: `${apiHostProtocol}://${apiHost}/${event.type.charAt(0)}`
    };
  }
  getEventUrl(metadataInfo) {
    const preferredBatchUrl = metadataInfo !== null && metadataInfo !== void 0 && metadataInfo.url ? metadataInfo.url : this.gasv3BatchUrl;
    const finalBatchUrl = this.useUrlFromMetadata ? preferredBatchUrl : this.gasv3BatchUrl;
    const isFallbackUrl = preferredBatchUrl === this.gasv3BatchUrl ? null : finalBatchUrl !== preferredBatchUrl;
    return [finalBatchUrl, isFallbackUrl];
  }
  cleanLibraryMetadata(obj) {
    // skip if the parameter is not an object
    if (obj === null || typeof obj !== 'object') {
      return;
    }
    for (const key in obj) {
      // Skip cleaning up props object and its keys
      // to avoid having to check the existence of the fields in props object
      // and manipulate them to calculate metadata signature in analytics service
      if (key === 'props') {
        continue;
      }
      let value = obj[key];
      // Remove null or undefined values
      if (value === null || value === undefined) {
        delete obj[key];
      }
      // Remove zero-valued numbers
      else if (typeof value === 'number' && value === 0) {
        delete obj[key];
      }
      // Remove empty arrays
      else if (Array.isArray(value)) {
        if (value.length === 0) {
          delete obj[key];
        } else {
          value.forEach(item => {
            this.cleanLibraryMetadata(item);
          });
        }
      }
      // Recurse into objects
      else if (typeof value === 'object') {
        this.cleanLibraryMetadata(obj[key]);
      }
    }
  }
  async invokeLocalResponseCallbackAsync(items, response, error) {
    const localResponseCallback = this.responseCallback;
    if (localResponseCallback) {
      // If we have a response, we'll parse it and create an AnalyticsAPIResponse from it.
      let analyticsAPIResponse = null;
      if (response) {
        var _responseObject$messa;
        let responseObject;
        try {
          responseObject = await response.clone().json();
        } catch (error) {
          // Ignore errors parsing the response, we can't do anything about them
        }

        // Ensure we've got a valid object to work with, filling in any missing
        // properties as best we can.
        responseObject = responseObject || {};
        responseObject.code = Number.parseInt(responseObject.code) > 0 ? Number.parseInt(responseObject.code) : response.status;
        responseObject.success = responseObject.success || responseObject.code >= 200 && responseObject.code <= 299;
        analyticsAPIResponse = new AnalyticsAPIResponse(responseObject.success, responseObject.validationErrors || new Map(), new Message(((_responseObject$messa = responseObject.message) === null || _responseObject$messa === void 0 ? void 0 : _responseObject$messa.error) || ''), responseObject.code, responseObject.validationReports || null);
      }

      // If we have an error, but it's not an instance of Error (e.g. it's a string), we'll convert it to an Error object.
      if (error && !(error instanceof Error)) {
        error = new Error(String(error));
      }
      const analyticsResponse = BatchInterceptor.toAnalyticsResponse(items || [], error || null,
      // In case 'error' were to be undefined we'll convert it to null.
      analyticsAPIResponse);
      localResponseCallback(analyticsResponse);
    }
  }
  async attachXIDs(items) {
    if (this.xidPromiseCallback) {
      return attachXidToMultipleEvents(items, this.xidPromiseCallback);
    }
    return Promise.resolve(items);
  }
  setResponseCallback(responseCallback) {
    this.responseCallback = responseCallback;
  }
  refreshMetadata(userInfo, tenantInfo) {
    this.metadataClient.userInfo = userInfo;
    this.metadataClient.tenantInfo = tenantInfo;
  }
}
class BatchInterceptor {
  static toAnalyticsResponse(items, error, analyticsAPIResponse) {
    const eventResponseList = [];
    if (analyticsAPIResponse !== null) {
      const [statusCode, success] = [analyticsAPIResponse.code, analyticsAPIResponse.success];
      if (statusCode >= 200 && statusCode <= 299) {
        var _analyticsAPIResponse;
        (_analyticsAPIResponse = analyticsAPIResponse.validationReports) === null || _analyticsAPIResponse === void 0 || _analyticsAPIResponse.forEach(report => {
          const matchedEvent = items.find(item => item.msg.messageId === report.messageId);
          if (matchedEvent) {
            eventResponseList.push(new EventResponse(matchedEvent.msg.messageId, report.results, matchedEvent.msg));
          }
        });
        return new AnalyticsResponse(success, statusCode, HTTP_STATUS_CODE_MAP.get(statusCode), eventResponseList);
      } else if (statusCode >= 400 && statusCode <= 499) {
        var _analyticsAPIResponse2;
        // not exluding "HTTP 429 Too Many Requests error" as it is thrown as an error and handled below
        const errorMessage = ((_analyticsAPIResponse2 = analyticsAPIResponse.message) === null || _analyticsAPIResponse2 === void 0 ? void 0 : _analyticsAPIResponse2.error) !== null ? analyticsAPIResponse.message.error : HTTP_STATUS_CODE_MAP.has(statusCode) ? HTTP_STATUS_CODE_MAP.get(statusCode) : HTTP_STATUS_CODE_MAP.get(-1);
        items.forEach(item => {
          eventResponseList.push(new EventResponse(item.msg.messageId, [], item.msg));
        });
        return new AnalyticsResponse(success, statusCode, errorMessage, eventResponseList);
      }
    } else if (error !== null && error !== void 0 && error.message) {
      const match = error.message.match(BatchInterceptor.http_error_regex);
      if (match) {
        items.forEach(item => {
          eventResponseList.push(new EventResponse(item.msg.messageId, [], item.msg));
        });
        return new AnalyticsResponse(false, Number(match[1]), match[2], eventResponseList);
      }
    }
    items.forEach(item => {
      eventResponseList.push(new EventResponse(item.msg.messageId, [], item.msg));
    });
    return new AnalyticsResponse(false, -1, HTTP_STATUS_CODE_MAP.get(-1), eventResponseList);
  }
}
_defineProperty(BatchInterceptor, "http_error_regex", /HTTP Error\s([0-9]{3})\s\((.+)\)/);